import { isLearnSurface } from '@edusense/shared-constants';
import React, { useState, useEffect } from 'react';
import { 
  course_courses_types_roadmaps,
  course_courses_types_courses,
  course_courses_loading_courses,
  course_courses_loading_roadmaps,
  course_courses_empty_courses,
  course_courses_empty_roadmaps,
  course_courses_error_loadFailed,
  course_courses_loadMore_courses
} from '@paraglide/messages';

import { TabNav, TabItem } from '@edusense/shared-ui-layout';
import { StatusDisplay } from '@edusense/shared-ui-kit';
import { RoadmapList } from '@edusense/features-roadmap';
import { CourseList } from '@edusense/features-course';
import { useCourseData, useRoadmapData } from '@edusense/features-course-hooks';
import { GetApiCoursesType } from '@edusense/course-client';
import { useAuth } from '@edusense/shared-auth-core';
import { useReport } from '@edusense/api-hooks';
import { toast } from 'react-hot-toast';

/**
 * 강의/로드맵 페이지 메인 컴포넌트 (라이브러리 컴포넌트 사용)
 */
function CoursesPage() {
  // 🚨 CRITICAL: Track if CoursesPage is actually rendering
  console.log('🔥🔥🔥 CoursesPage RENDER:', `/ko/learn at ${Date.now()}`);
  console.log('🔥 CoursesPage: Component re-rendered successfully!');
  
  
  const [activeTab, setActiveTab] = useState<'courses' | 'roadmaps'>(isLearnSurface() ? 'courses' : 'roadmaps');
  const { isAuthenticated, token } = useAuth();

  // 신고 mutation hook
  const { mutateAsync: reportMutation } = useReport();

  // 강의 데이터 가져오기
  const coursesData = useCourseData({
    type: GetApiCoursesType.FORYOU,
    enabled: activeTab === 'courses'
  });

  // 로드맵 데이터 가져오기
  const roadmapsData = useRoadmapData({
    type: 'Featured',
    enabled: activeTab === 'roadmaps'
  });

  // 신고 핸들러
  const handleReport = async (targetId: string, data: { reason: string; description?: string }, targetType: 'course' | 'roadmap') => {
    try {
      await reportMutation({
        targetType,
        targetId,
        data: {
          reason: data.reason as any,
          description: data.description,
        },
      });
      toast.success('신고가 접수되었습니다. 검토 후 조치하겠습니다.');
    } catch (error) {
      toast.error('신고 접수 중 오류가 발생했습니다. 다시 시도해주세요.');
      throw error;
    }
  };

  // 인증 상태 확인
  useEffect(() => {
    console.log('[CoursesPage] 인증 상태:', isAuthenticated, '토큰 있음:', !!token);
  }, [isAuthenticated, token]);

  // 탭 전환 시 데이터 갱신
  useEffect(() => {
    if (activeTab === 'courses') {
      console.log('[CoursesPage] 강의 데이터 초기 로드');
      coursesData.refresh();
    } else if (activeTab === 'roadmaps') {
      console.log('[CoursesPage] 로드맵 데이터 초기 로드');
      roadmapsData.refresh();
    }
  }, [activeTab]);

  // 탭 정의
  const courseTabs: TabItem[] = [
    { id: 'roadmaps', label: course_courses_types_roadmaps() },
    { id: 'courses', label: course_courses_types_courses() }
  ];

  // 탭 변경 핸들러
  const handleTabChange = (tabId: string) => {
    console.log(`탭 변경: ${tabId}`);
    setActiveTab(tabId as 'courses' | 'roadmaps');
  };

  // 강의 컨텐츠 렌더링
  const renderCoursesContent = () => {
    const courses = coursesData.data || [];
    
    if (coursesData.isLoading && courses.length === 0) {
      return <StatusDisplay type="loading" message={course_courses_loading_courses()} />;
    }
    
    if (coursesData.error) {
      const error = coursesData.error as any;
      return (
        <StatusDisplay 
          type="error" 
          message={course_courses_error_loadFailed()} 
        />
      );
    }
    
    if (courses.length === 0 && !coursesData.isLoading) {
      return <StatusDisplay type="empty" message={course_courses_empty_courses()} />;
    }
    
    return (
      <>
        <CourseList
          courses={courses}
          isLoading={coursesData.isLoading}
          isFetchingNextPage={coursesData.isFetchingNextPage}
          error={coursesData.error}
          hasNextPage={coursesData.hasNextPage}
          fetchNextPage={coursesData.fetchNextPage}
          observerRef={coursesData.observerRef}
          emptyStateContent={course_courses_empty_courses()}
          onReport={(courseId, data) => handleReport(courseId, data, 'course')}
        />
        
        {/* 수동 로드 버튼 (무한 스크롤이 작동하지 않을 때를 위한 대체 UI) */}
        {coursesData.hasNextPage && courses.length > 0 && !coursesData.isFetchingNextPage && (
          <div style={{ textAlign: 'center', padding: '1rem 0 2rem' }}>
            <button
              onClick={() => coursesData.fetchNextPage()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--theme-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              {course_courses_loadMore_courses()}
            </button>
          </div>
        )}
      </>
    );
  };

  // 로드맵 컨텐츠 렌더링
  const renderRoadmapsContent = () => {
    const roadmaps = roadmapsData.data || [];

    // 🚨 강제 디버깅 로그
    console.log('🚨🚨🚨 renderRoadmapsContent:', {
      roadmapsLength: roadmaps.length,
      roadmapsDataType: typeof roadmapsData.data,
      roadmapsData: roadmapsData.data,
      isLoading: roadmapsData.isLoading,
      error: roadmapsData.error,
      activeTab
    });

    if (roadmapsData.isLoading && roadmaps.length === 0) {
      console.log('🚨 Showing LOADING state');
      return <StatusDisplay type="loading" message={course_courses_loading_roadmaps()} />;
    }

    if (roadmapsData.error) {
      console.log('🚨 Showing ERROR state');
      return <StatusDisplay
        type="error"
        message={course_courses_error_loadFailed()}
      />;
    }

    console.log('🚨 Rendering RoadmapList with roadmaps:', roadmaps);

    return (
      <RoadmapList
        roadmaps={roadmaps}
        isLoading={roadmapsData.isLoading}
        isFetchingNextPage={roadmapsData.isFetchingNextPage}
        error={roadmapsData.error}
        hasNextPage={roadmapsData.hasNextPage}
        fetchNextPage={roadmapsData.fetchNextPage}
        observerRef={roadmapsData.observerRef}
        emptyStateContent={course_courses_empty_roadmaps()}
        onReport={(roadmapId, data) => handleReport(roadmapId, data, 'roadmap')}
      />
    );
  };

  // 컨텐츠 렌더링
  const renderContent = () => {
    if (activeTab === 'courses') {
      return renderCoursesContent();
    }
    
    if (activeTab === 'roadmaps') {
      return renderRoadmapsContent();
    }
    
    return null;
  };

  // 상태 디버깅 로그
  useEffect(() => {
    if (activeTab === 'courses') {
      console.log('강의 데이터 상태:', {
        count: coursesData.data?.length || 0,
        isLoading: coursesData.isLoading,
        isFetchingNextPage: coursesData.isFetchingNextPage,
        hasNextPage: coursesData.hasNextPage
      });
    } else {
      console.log('로드맵 데이터 상태:', {
        count: roadmapsData.data?.length || 0,
        isLoading: roadmapsData.isLoading,
        isFetchingNextPage: roadmapsData.isFetchingNextPage,
        hasNextPage: roadmapsData.hasNextPage
      });
    }
  }, [
    activeTab,
    coursesData.data?.length,
    coursesData.isLoading,
    coursesData.isFetchingNextPage,
    coursesData.hasNextPage,
    roadmapsData.data?.length,
    roadmapsData.isLoading,
    roadmapsData.isFetchingNextPage,
    roadmapsData.hasNextPage
  ]);

  // 🚨 렌더링 확인 로깅 (DiscoverPage와 동일)
  console.log(`📚📚📚 CoursesPage RENDER: ${window.location.pathname} at ${Date.now()}`);
  console.log(`📚 CoursesPage: Component re-rendered successfully!`);
  
  // 🔥 임시 강제 디버깅 - 캐시 우회용
  if (Math.random() > 0.7) {
    console.log(`🔥🔥🔥 FORCE DEBUG: CoursesPage definitely rendered at ${Date.now()}`);
  }

  return (
    <div>
      {/* 탭 네비게이션 - MainPage와 동일한 스타일 */}
      <TabNav 
        tabs={courseTabs} 
        activeTabId={activeTab} 
        onTabChange={handleTabChange} 
        variant="indicator"
      />
      
      {/* 컨텐츠 영역 */}
      <div>
        {renderContent()}
      </div>
      
    </div>
  );
}

export default CoursesPage;
